import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { isAxiosError } from "axios";
import { Check, Copy, Download, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { openDownload } from "@/components/itsm-import/export-csv";
import { JobStatusBadge } from "@/components/itsm-import/job-status-badge";
import { useImportExportUrl, useImportJob } from "@/hooks/use-itsm-import";
import { ticketReportPrompt } from "@/lib/solution-engineer/ticket-report-prompt";

const CLAUDE_NEW_CHAT_URL = "https://claude.ai/new";

interface ClaudeReportHandoffProps {
  tenantId: string;
  jobId: string;
  itsmName: string;
}

/**
 * Follows one export job and hands its CSV to Claude: download the file, copy
 * the reports prompt, open a new chat and attach both there.
 */
export function ClaudeReportHandoff({
  tenantId,
  jobId,
  itsmName,
}: ClaudeReportHandoffProps) {
  const { data: job } = useImportJob(tenantId, jobId);
  const exportUrl = useImportExportUrl(tenantId);
  const [prompt, setPrompt] = useState(() => ticketReportPrompt(itsmName));
  const [copied, setCopied] = useState(false);

  const completed = job?.status === "completed";

  const onDownload = async () => {
    try {
      const { download_url } = await exportUrl.mutateAsync(jobId);
      openDownload(download_url);
    } catch (err: unknown) {
      // The job is an export, so a 404 means it wrote no file
      toast.error(
        isAxiosError(err) && err.response?.status === 404
          ? "The export found no tickets, so there is no CSV"
          : "Failed to get the CSV download link",
      );
    }
  };

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
      setTimeout(() => { setCopied(false); }, 2000);
    } catch {
      toast.error("Could not copy. Select the prompt and copy it manually.");
    }
  };

  return (
    <div className="space-y-4 rounded-md border p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm">
          {job ? <JobStatusBadge status={job.status} /> : <Loader2 className="h-4 w-4 animate-spin" />}
          {job && (
            <span className="text-muted-foreground">
              {job.fetched_tickets} tickets fetched
            </span>
          )}
          <Link
            to="/itsm-import/$jobId"
            params={{ jobId }}
            search={{ tenantId }}
            className="text-muted-foreground underline underline-offset-4"
          >
            Job details
          </Link>
        </div>
        <Button
          size="sm"
          onClick={() => { void onDownload(); }}
          disabled={!completed || exportUrl.isPending}
        >
          {exportUrl.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Download className="mr-2 h-4 w-4" />
          )}
          Download CSV
        </Button>
      </div>
      {job?.status === "failed" && job.error_message && (
        <p className="text-destructive text-sm">{job.error_message}</p>
      )}

      <div className="space-y-1.5">
        <Label htmlFor="report-prompt">Claude prompt for the PDF reports</Label>
        <Textarea
          id="report-prompt"
          value={prompt}
          onChange={(e) => { setPrompt(e.target.value); }}
          rows={10}
          className="font-mono text-xs"
        />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" onClick={() => { void onCopy(); }}>
          {copied ? <Check className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />}
          {copied ? "Copied" : "Copy prompt"}
        </Button>
        <Button size="sm" variant="outline" asChild>
          <a
            href={`${CLAUDE_NEW_CHAT_URL}?q=${encodeURIComponent(prompt)}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <ExternalLink className="mr-2 h-4 w-4" />
            Open Claude
          </a>
        </Button>
        <p className="text-muted-foreground text-xs">
          The new chat opens with the prompt filled in; attach the CSV and send.
        </p>
      </div>
    </div>
  );
}
